import { useContext } from 'react';

import { Trans } from '@lingui/react/macro';
import { Badge } from '@repo/videa-components/src/ui/badge';
import classNames from 'classnames';

import { ExternalPatientContext } from '@/app/external/constants';
import { usePreferences } from '@/react-query/usePreferences';

import type {
  TProcedure,
  TVisitTreatment,
} from '../../../../react-query/useVisitSummaries';

import { useGetTreatmentStatus, useIsBasicOdontogram } from './hooks';

interface Props {
  procedures?: TProcedure[];
  treatments?: TVisitTreatment[];
  hasAnalysis?: boolean;
  isIndividualTooth?: boolean;
  isDarkMode?: boolean;
  isPerioPatient?: boolean;
}

export const TreatmentStatusBadge = ({
  procedures,
  treatments,
  hasAnalysis,
  isIndividualTooth = false,
  isDarkMode,
  isPerioPatient,
}: Props) => {
  const { data: prefs } = usePreferences();
  const isBasicOdontogram = useIsBasicOdontogram();
  let treatmentStatus = useGetTreatmentStatus(
    procedures,
    treatments,
    prefs,
    isPerioPatient,
  );
  const { isIframe } = useContext(ExternalPatientContext);

  // TODO: In the future, we will want to show the no recent images badge in the
  // iframe context to match VideaAI.
  if (isIframe) {
    return null;
  }

  // In the basic odontogram, we never want to show the treatment status badge,
  // but we want to show the no recent images badge when not in iframe context.
  let badge;
  if (isBasicOdontogram) {
    treatmentStatus = undefined;
  }

  switch (treatmentStatus) {
    case 'scheduled_today':
      badge = (
        <Badge
          className={classNames(
            'vai-rounded-xl vai-py-1 vai-px-2 vai-font-inter',
            {
              'vai-bg-slate-400 hover:vai-bg-slate-400': !isDarkMode,
              'vai-bg-slate-500 hover:vai-bg-slate-500': isDarkMode,
            },
          )}
        >
          <p>
            <Trans>Tx scheduled for today</Trans>
          </p>
        </Badge>
      );

      break;
    case 'completed':
      badge = (
        <Badge className="!vai-bg-aqua-400 hover:!vai-bg-aqua-400 vai-rounded-xl vai-py-1 vai-px-2 vai-font-inter">
          <p>
            <Trans>Tx completed</Trans>
          </p>
        </Badge>
      );

      break;
    case 'scheduled_future':
      badge = (
        <Badge
          className={classNames(
            'vai-rounded-xl vai-py-1 vai-px-2 vai-font-inter',
            {
              'vai-bg-slate-400 hover:vai-bg-slate-400': !isDarkMode,
              'vai-bg-slate-500 hover:vai-bg-slate-500': isDarkMode,
            },
          )}
        >
          <p>
            <Trans>Tx scheduled</Trans>
          </p>
        </Badge>
      );

      break;
    case 'unscheduled':
      badge = (
        <Badge className="vai-bg-red-500 hover:vai-bg-red-500 vai-rounded-xl vai-py-1 vai-px-2 vai-font-inter">
          <p>
            <Trans>Tx not scheduled</Trans>
          </p>
        </Badge>
      );

      break;
    case 'recommended':
      badge = (
        <Badge
          className={classNames(
            'vai-rounded-xl vai-py-1 vai-px-2 vai-font-inter',
            {
              'vai-bg-purple-700 hover:vai-bg-purple-700': !isDarkMode,
              'vai-bg-violet-700 hover:vai-bg-violet-700': isDarkMode,
            },
          )}
        >
          <p>
            <Trans>Tx opportunity</Trans>
          </p>
        </Badge>
      );

      break;
    default:
      badge = null;
  }

  if (isIndividualTooth && !hasAnalysis) {
    badge = (
      <div className="vai-flex vai-items-center vai-gap-1">
        {badge}
        <span
          className={classNames('vai-font-inter vai-text-[12px] vai-italic', {
            'vai-text-slate-800': !isDarkMode,
            'vai-text-slate-100': isDarkMode,
          })}
        >
          <Trans>No Recent Images</Trans>
        </span>
      </div>
    );
  }

  return badge;
};
